import {BoxTraverser} from '@mintdata-wsp/common/controllers/surface/BoxVisitor';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {LockComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/lock/LockComponent';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {VisibilityEyeComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/visibility/VisibilityEyeComponent';
import {MouseOverRef} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Input, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {Scrollable, ScrollBehavior} from '@mintdata-wsp/wsp/ui/components/scrollable/Scrollable';
import {SurfaceAreaContextMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/contextMenu/SurfaceAreaContextMenu';
import {SurfaceKeyboardShortcuts} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceKeyboardShortcuts';
import {SurfaceOutlinePanelPartWrapper} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/SurfaceOutlinePanelPartWrapper';
import {BoxHostId, BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {Section} from '@mintdata/mint-kit/impl/properties/components/section/Section';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./SurfaceBoxesOutline.less');
const cx = classnames.bind(styles);

enum BoxDropPlace
{
    Above = 'Above',
    Below = 'Below',
    Into = 'Into'
}

interface DragOverBoxCtx
{
    boxId:BoxId;
    hostId:BoxHostId;
    boxDropPlace:BoxDropPlace;
}

interface RenamingBoxCtx
{
    boxId:BoxId;
    newValue:string;
}

export interface ISurfaceBoxesOutlineState
{
    renamingBoxCtx:RenamingBoxCtx;
    isDragging:boolean;
    dragOverBoxCtx:DragOverBoxCtx;
    dropPlace:BoxDropPlace;
    draggingBoxesPosition:Map<BoxId, Point>;
    itemRefs:Map<BoxId, HTMLElement>;
    openBoxTimer:number;
}

interface SurfaceBoxesOutlineProps
{
    onExpand?:() => void,
    onCollapse?:() => void,
}

@observer
export class SurfaceBoxesOutline extends React.Component<SurfaceBoxesOutlineProps, ISurfaceBoxesOutlineState>
{
    @observable
    private _state:ISurfaceBoxesOutlineState = {
        renamingBoxCtx: {
            boxId: null,
            newValue: null
        },
        isDragging: false,
        dragOverBoxCtx: {
            boxId: null,
            hostId: null,
            boxDropPlace: null
        },
        dropPlace: null,
        draggingBoxesPosition: new Map(),
        itemRefs: new Map(),
        openBoxTimer: null
    };

    private static START_DRAGGING_AFTER_DELTA = 4;

    private _refHandlers = {
        forItem: (boxId:BoxId):(ref:HTMLDivElement | null) => void => (
            (ref:HTMLDivElement | null) =>
            {
                if( ref )
                    this._state.itemRefs.set(boxId, ref);
                else
                    this._state.itemRefs.delete(boxId);
            }
        )
    };

    public componentDidMount()
    {
        document.addEventListener('mousedown', this._handleDocumentClick, false);
    }

    public componentWillUnmount()
    {
        document.removeEventListener('mousedown', this._handleDocumentClick, false);
    }

    @action
    private _handleDocumentClick = (event:MouseEvent) =>
    {
        const {renamingBoxCtx, itemRefs} = this._state;

        if( renamingBoxCtx.boxId )
        {
            const renamingBoxRef = itemRefs.get(renamingBoxCtx.boxId);

            if(
                renamingBoxRef &&
                renamingBoxRef !== event.target &&
                renamingBoxRef.contains(event.target as any)
            )
                return;

            // handle click that happened outside the renaming box
            this._renameBox(renamingBoxCtx.boxId);
        }
    };

    public render():JSX.Element
    {
        const {surfaceArea, workbenchVisibility} = controllers.ui;
        const currentLayerPage:Page = surfaceArea.getCurrentLayerPage();

        if( !currentLayerPage )
            return null;

        if( surfaceArea.isInPreviewOrZenPreviewMode() )
            return this._renderInPreviewMode();

        const {boxes} = currentLayerPage.artboard;
        const isCollapsed:boolean = workbenchVisibility.isSurfaceBoxesOutlinePanelCollapsed();

        return (
            <SurfaceOutlinePanelPartWrapper
                className={cx('wrapper')}
                title="Outline"
                isCollapsed={isCollapsed}
                onCollapseChanged={this._setSurfaceBoxesOutlineCollapsed}
            >
                <SurfaceKeyboardShortcuts {...this._getKeyboardHandlerProps()}>
                    <Scrollable
                        isScrollX={true}
                        isScrollY={true}
                        className={cx('base')}
                        behavior={ScrollBehavior.OnHover}
                    >
                        {
                            boxes.length ? (
                                this._renderItems(boxes)
                            ) : (
                                <Section>
                                    Please add a component to the surface to see the outline.
                                </Section>
                            )
                        }
                    </Scrollable>
                </SurfaceKeyboardShortcuts>
            </SurfaceOutlinePanelPartWrapper>
        );
    }

    private _renderInPreviewMode():JSX.Element
    {
        return (
            <Section>
                Preview mode.
            </Section>
        );
    }

    private _renderItems(boxes:Box[]):JSX.Element[]
    {
        return boxes.map(this._renderItem).reverse();
    }

    private _renderHosts(box:Box):JSX.Element[]
    {
        const {surfaceArea, surfacePagesPanel} = controllers.ui;

        if( box.componentDesc.isMultiHostContainer )
            return box.componentDesc.containerHostIds.map(
                (hostId:BoxHostId, hostIndex:number) =>
                {
                    const isOpen:boolean = surfacePagesPanel.isOutlineItemOpen(box.id, hostId);
                    const hostTitle:string = box.componentDesc.containerHostTitles[hostIndex];
                    const itemName:string = hostTitle !== hostId
                        ? `${hostTitle}` // (${hostId})
                        : hostId;
                    const itemPaddingStyle:React.CSSProperties = SurfaceBoxesOutline._calculateItemPadding(box, true);
                    const isHighlighted:boolean = this._isBoxHighlighted(box, hostId);
                    const leftIcons:Array<JSX.Element> = this._getItemLeftIcons(box, hostId);
                    const isFirstHost:boolean = surfaceArea.isFirstHost(box, hostId);

                    return (
                        <div key={`${box.id}-${hostId}-group`}>
                            <div
                                data-box-id={box.id}
                                data-host-id={hostId}
                                className={cx('item', 'hostItem', {isHighlighted, isHostFirst: isFirstHost})}
                                style={{...itemPaddingStyle}}
                                onMouseOver={this._getItemMouseOverHandler(box)}
                                onMouseLeave={this._getItemMouseLeaveHandler(box)}
                                onDoubleClick={this._getItemDoubleClickHandler(box)}
                            >
                                {leftIcons}
                                {itemName}
                                {this._renderDropIntoPlaceholder(box, hostId)}
                                {isFirstHost && this._renderDropBottomPlaceholders(box, hostId)}
                            </div>
                            {isOpen && this._renderItems(box.children.filter(
                                (childBox:Box) => childBox.hostId === hostId
                            ))}
                        </div>
                    );
                }
            );
        else
            return this._renderItems(box.children);
    }

    private _renderItem = (box:Box):JSX.Element =>
    {
        const {surfaceSelection, surfacePagesPanel} = controllers.ui;
        const {isDragging, renamingBoxCtx} = this._state;

        const itemName:JSX.Element | string = renamingBoxCtx.boxId === box.id
            ? this._renderRenameEditor(box)
            : this._getItemName(box);
        const itemPaddingStyle:React.CSSProperties = SurfaceBoxesOutline._calculateItemPadding(box, false);
        const isSelected:boolean = surfaceSelection.isBoxSelected(box);
        const isHighlighted:boolean = this._isBoxHighlighted(box, null);
        const leftIcons:Array<JSX.Element> = this._getItemLeftIcons(box, null);

        const boxVisibilityProp:Prop<ABoolean> = box.componentDesc.visibilityProp;
        const boxLockProp:Prop<ABoolean> = box.componentDesc.lockProp;
        const isVisibilityBound:boolean = !!boxVisibilityProp.getBindingId(PropPath.ROOT);

        const isLocked = boxLockProp.value.value;

        const item:JSX.Element = (
            <SurfaceAreaContextMenu key={box.id} className={cx('itemContextMenu')}>
                <div className={cx('itemWrapper')}>
                    <DraggableCore
                        onStart={this._getItemDragStartHandler(box)}
                        onDrag={this._getItemDragHandler(box)}
                        onStop={this._getItemDragStopHandler(box, null)}
                        onMouseDown={this._getItemMouseDownHandler(box)}
                        enableUserSelectHack={false}
                    >
                        <div
                            ref={this._refHandlers.forItem(box.id)}
                            data-box-id={box.id}
                            className={cx('item', {isSelected, isDragging, isHighlighted})}
                            style={{...itemPaddingStyle}}
                            onMouseOver={this._getItemMouseOverHandler(box)}
                            onMouseLeave={this._getItemMouseLeaveHandler(box)}
                            onDoubleClick={this._getItemDoubleClickHandler(box)}
                        >
                            {leftIcons}
                            <span className={styles.name}>{itemName}</span>
                            {this._renderDropTopPlaceholder(box)}
                            {this._renderDropIntoPlaceholder(box, null)}
                            {this._renderDropBottomPlaceholders(box, null)}

                            {
                                !box.isViewport && isLocked &&
                                <LockComponent
                                    className={cx('lock')}
                                    value={boxLockProp.value.value}
                                    onClick={this._handleLockClick(boxLockProp)}
                                />
                            }
                            {!box.isViewport && <VisibilityEyeComponent
                                className={cx('visibilityEye', {bound: isVisibilityBound})}
                                value={boxVisibilityProp.value.value}
                                onClick={this._handleVisibilityEyeClick(boxVisibilityProp, isVisibilityBound)}
                                onDoubleClick={this._handleVisibilityEyeDoubleClick}
                            />}
                        </div>
                    </DraggableCore>

                    {this._renderDraggingElement(box)}
                </div>
            </SurfaceAreaContextMenu>
        );

        if( box.children.length || box.componentDesc.isMultiHostContainer )
        {
            const isOpen:boolean = surfacePagesPanel.isOutlineItemOpen(box.id, null);

            return (
                <div key={`${box.id}-group`}>
                    {item}
                    {isOpen && this._renderHosts(box)}
                </div>
            );
        }

        return item;
    };

    private _getItemLeftIcons(box:Box, hostId:BoxHostId):JSX.Element[]
    {
        const {surfacePagesPanel, componentIcons} = controllers.ui;

        const icons = [];

        if( !hostId )
        {
            const componentIcon = componentIcons.getIconForVisualComponent(
                box.component,
                WSPIconSize.medium16,
                cx('componentIcon')
            );

            icons.push(componentIcon);
        }

        const childrenCount:number = box.componentDesc.isMultiHostContainer && !hostId
            ? box.componentDesc.containerHostIds.length
            : box.componentDesc.isMultiHostContainer
                ? box.children.filter((childBox:Box) => childBox.hostId === hostId).length
                : box.children.length;

        if( childrenCount > 0 )
        {
            const isOpen = surfacePagesPanel.isOutlineItemOpen(box.id, hostId);

            const collapseIcon = (
                <WSPIcon
                    key={'collapse-icon'}
                    className={cx('collapseIcon')}
                    name={isOpen ? WSPIcons.common.ArrowDown : WSPIcons.common.ArrowRight}
                    size={WSPIconSize.medium16}
                    onMouseDown={this._getCollapseIconMouseDownHandler(box.id, hostId)}
                />
            );

            icons.push(collapseIcon);
        }

        return icons;
    }

    private _getItemName(box:Box):string
    {
        const {surfaceArea} = controllers.ui;

        return surfaceArea.getBoxName(box);
    }

    private _renderRenameEditor(box:Box):JSX.Element
    {
        const {renamingBoxCtx} = this._state;

        return (
            <Input
                className={cx('renameEditor')}
                inputClassName={cx('renameEditorInput')}
                style={InputStyle.Simple}
                initialValue={this._getItemName(box)}
                isInitiallySelected={true}
                isInitiallyFocused={true}
                onChange={(newValue:string) => renamingBoxCtx.newValue = newValue}
                onEnterKey={() => this._renameBox(box.id)}
            />
        );
    }

    @action
    private _renameBox(boxId:BoxId)
    {
        const {surfaceArea} = controllers.ui;
        const {renamingBoxCtx} = this._state;

        const currentLayerPage:Page = surfaceArea.getCurrentLayerPage();
        const box = surfaceArea.findBoxById(currentLayerPage.id, boxId);

        if( renamingBoxCtx.newValue )
        {
            box.componentDesc.nameProp.setInnerValue(AString.fromString(renamingBoxCtx.newValue));

            box.componentDesc.removeLinkNameToProp();
        }

        this._closeRenameEditor();
    }

    @action
    private _closeRenameEditor()
    {
        const {renamingBoxCtx} = this._state;

        renamingBoxCtx.boxId = null;
        renamingBoxCtx.newValue = null;
    }

    @bind
    private _setSurfaceBoxesOutlineCollapsed(isCollapsed:boolean):void
    {
        const {onExpand, onCollapse} = this.props;
        controllers.ui.workbenchVisibility.setSurfaceBoxesOutlinePanelCollapsed(isCollapsed);

        if( isCollapsed && onCollapse )
            onCollapse();
        else if( onExpand )
            onExpand();
    }

    // only for the first item in a subtree
    private _renderDropTopPlaceholder(box:Box):JSX.Element
    {
        const {isDragging, dragOverBoxCtx} = this._state;

        if( isDragging )
        {
            if( !this._isDropAboveBoxAllowed(box) )
                return null;

            const dropLineOffsetStyle = this._calculateDropLineOffset(box, null);

            return (
                <div
                    className={cx('dropTopPlaceholder')}
                    onMouseOver={this._getPlaceholderMouseOverHandler(box.id, null, BoxDropPlace.Above)}
                    onMouseLeave={this._getPlaceholderMouseLeaveHandler(box.id, null, BoxDropPlace.Above)}
                >
                    {
                        dragOverBoxCtx.boxId === box.id &&
                        dragOverBoxCtx.boxDropPlace === BoxDropPlace.Above &&
                        <div
                            className={cx('dropLine')}
                            style={{...dropLineOffsetStyle}}
                        />
                    }
                </div>
            );
        }

        return null;
    }

    private _isDropAboveBoxAllowed(box:Box):boolean
    {
        const {surfaceArea} = controllers.ui;

        return !surfaceArea.boxHasSelectedParent(box);
    }

    private _renderDropIntoPlaceholder(box:Box, hostId:BoxHostId):JSX.Element
    {
        const {isDragging} = this._state;

        if( isDragging )
        {
            if( !this._isDropIntoBoxAllowed(box) )
                return null;

            return (
                <div
                    className={cx('dropIntoPlaceholder')}
                    onMouseOver={this._getPlaceholderMouseOverHandler(box.id, hostId, BoxDropPlace.Into)}
                    onMouseLeave={this._getPlaceholderMouseLeaveHandler(box.id, hostId, BoxDropPlace.Into)}
                    onMouseMove={this._getPlaceholderMouseMoveHandler(box.id, hostId, BoxDropPlace.Into)}
                />
            );
        }

        return null;
    }

    private _isDropIntoBoxAllowed(box:Box):boolean
    {
        const {surfaceArea, surfaceSelection} = controllers.ui;

        return surfaceArea.isBoxContainer(box) &&
            !surfaceSelection.isBoxSelected(box) &&
            !surfaceArea.boxHasSelectedParent(box);
    }

    private _renderDropBottomPlaceholders(box:Box, hostId:BoxHostId):JSX.Element[]
    {
        const {surfaceArea, surfacePagesPanel} = controllers.ui;
        const {isDragging} = this._state;

        if( isDragging )
        {
            const placeholders:JSX.Element[] = [];

            if( !surfacePagesPanel.isOutlineItemOpen(box.id, hostId)
                || (!box.children.length
                    && (!box.componentDesc.isMultiHostContainer || surfaceArea.isFirstHost(box, hostId))) )
            {
                if( this._isDropBelowBoxAllowed(box) )
                    placeholders.push(this._renderDropBottomPlaceholder(box, null));

                if( surfaceArea.isBoxFirst(box) )
                {
                    let hasNotFirstBox:boolean = false;
                    let prevHostId:BoxHostId = box.hostId;

                    BoxTraverser.visit(box, {
                        onBox: (parent:Box) =>
                        {
                            if( !surfaceArea.isBoxFirst(parent) )
                                hasNotFirstBox = true;

                            if( this._isDropBelowBoxAllowed(parent)
                                && (surfaceArea.isBoxFirst(parent) && !hasNotFirstBox
                                    || (prevHostId && surfaceArea.isFirstHost(parent, prevHostId)
                                        && parent.componentDesc.isMultiHostContainer)) )
                                placeholders.push(this._renderDropBottomPlaceholder(parent, null));

                            prevHostId = parent.hostId;
                        }
                    }, true);
                }
            }

            return placeholders.reverse();
        }

        return null;
    }

    private _renderDropBottomPlaceholder(box:Box, hostId:BoxHostId):JSX.Element
    {
        const {surfaceArea} = controllers.ui;
        const {dragOverBoxCtx} = this._state;

        const dropLineOffsetStyle = this._calculateDropLineOffset(box, hostId);
        const isBoxRoot = surfaceArea.isBoxRoot(box);

        const placeHolderStyle = {left: isBoxRoot ? 0 : dropLineOffsetStyle.left};
        const dropLineStyle = isBoxRoot ? {...dropLineOffsetStyle} : {...dropLineOffsetStyle, left: 0};

        return (
            <div
                key={`${box.id}-placeholder`}
                className={cx('dropBottomPlaceholder')}
                onMouseOver={this._getPlaceholderMouseOverHandler(box.id, hostId, BoxDropPlace.Below)}
                onMouseLeave={this._getPlaceholderMouseLeaveHandler(box.id, hostId, BoxDropPlace.Below)}
                style={placeHolderStyle}
            >
                {
                    dragOverBoxCtx.boxId === box.id &&
                    // dragOverBoxCtx.hostId === box.hostId &&
                    dragOverBoxCtx.boxDropPlace === BoxDropPlace.Below &&
                    <div
                        className={cx('dropLine')}
                        style={dropLineStyle}
                    />
                }
            </div>
        );
    }

    private _isDropBelowBoxAllowed(box:Box):boolean
    {
        const {surfaceArea, surfaceSelection} = controllers.ui;

        if( surfaceSelection.isBoxSelected(box) ||
            surfaceArea.boxHasSelectedParent(box) )
            return false;

        return true;
    }

    private _isBoxHighlighted(box:Box, hostId:BoxHostId):boolean
    {
        const {surfaceArea, surfacePagesPanel} = controllers.ui;
        const {dragOverBoxCtx} = this._state;

        const currentLayerPage:Page = surfaceArea.getCurrentLayerPage();
        const dragOverBox:Box = surfaceArea.findBoxById(currentLayerPage.id, dragOverBoxCtx.boxId);
        const isOpen = surfacePagesPanel.isOutlineItemOpen(box.id, hostId);
        let isHighlighted = false;

        if( dragOverBoxCtx.boxDropPlace !== BoxDropPlace.Into &&
            dragOverBox &&
            dragOverBox.parent &&
            dragOverBox.parent === box
            && (!dragOverBox.parent.componentDesc.isMultiHostContainer || dragOverBox.hostId === hostId) )
            isHighlighted = true;

        if(
            dragOverBoxCtx.boxId === box.id &&
            ((dragOverBoxCtx.hostId && dragOverBoxCtx.hostId === hostId)
                || (!dragOverBoxCtx.hostId && (!box.componentDesc.isMultiHostContainer
                    || (!isOpen && !hostId)))) &&
            dragOverBoxCtx.boxDropPlace === BoxDropPlace.Into
        )
            isHighlighted = true;

        return isHighlighted;
    }

    private _calculateDropLineOffset(box:Box, hostId:BoxHostId):React.CSSProperties
    {
        const itemPaddingStyle = SurfaceBoxesOutline._calculateItemPadding(box, !!hostId);

        return {
            left: itemPaddingStyle.paddingLeft - 12,
            right: 8
        };
    }

    private _renderDraggingElement(box:Box):JSX.Element
    {
        const {surfaceSelection} = controllers.ui;
        const {isDragging} = this._state;

        const itemName = this._getItemName(box);
        const isSelected = surfaceSelection.isBoxSelected(box);
        const itemPaddingStyle = SurfaceBoxesOutline._calculateItemPadding(box, false);

        if( isDragging && isSelected )
        {
            return (
                <div
                    className={cx('draggingElement')}
                    style={{
                        ...itemPaddingStyle,
                        left: this._state.draggingBoxesPosition.get(box.id).x,
                        top: this._state.draggingBoxesPosition.get(box.id).y
                    }}
                >
                    {itemName}
                </div>
            );
        }

        return null;
    }

    private _getKeyboardHandlerProps = () =>
    {
        const {surfaceKeyboard} = controllers.ui;

        const keyMap = surfaceKeyboard.getKeyMap();
        const keyHandlers = surfaceKeyboard.getKeyHandlers();

        return {
            keyMap: {
                ...keyMap,
                _esc: ['esc']
            },
            keyHandlers: {
                ...keyHandlers,
                _esc: () =>
                {
                    this._stopDrag();
                    this._closeRenameEditor();
                }
            }
        };
    };

    private _getPlaceholderMouseOverHandler = (boxId:BoxId,
                                               hostId:BoxHostId,
                                               boxDropPlace:BoxDropPlace) =>
    {
        return action((event:React.MouseEvent<HTMLDivElement>) =>
        {
            if( this._state.isDragging )
                this._state.dragOverBoxCtx = {boxId, hostId, boxDropPlace};
        });
    };

    private _getPlaceholderMouseLeaveHandler = (boxId:BoxId,
                                                hostId:BoxHostId,
                                                boxDropPlace:BoxDropPlace) =>
    {
        return action((event:React.MouseEvent<HTMLDivElement>) =>
        {
            if( this._state.isDragging )
            {
                this._state.dragOverBoxCtx = {
                    boxId: null,
                    hostId: null,
                    boxDropPlace: null
                };
            }
        });
    };

    @action
    private _getPlaceholderMouseMoveHandler = (boxId:BoxId,
                                               hostId:BoxHostId,
                                               boxDropPlace:BoxDropPlace) =>
    {
        if( boxDropPlace === BoxDropPlace.Into )
        {
            if( this._state.openBoxTimer )
                clearTimeout(this._state.openBoxTimer);

            return (event:React.MouseEvent<HTMLDivElement>) =>
            {
                this._state.openBoxTimer = window.setTimeout(action((event:React.MouseEvent<HTMLDivElement>) =>
                {
                    const {surfacePagesPanel} = controllers.ui;
                    const {isDragging, dragOverBoxCtx} = this._state;

                    if( isDragging )
                    {
                        if( dragOverBoxCtx.boxId === boxId
                            && dragOverBoxCtx.hostId === hostId
                            && !surfacePagesPanel.isOutlineItemOpen(boxId, hostId) )
                            surfacePagesPanel.openOutlineItem(boxId, hostId);
                    }
                }), 1000);
            };
        }
    };

    private _getItemDragStartHandler = (box:Box):(event:MouseEvent, data:DraggableData) => void => (
        action((event:MouseEvent, data:DraggableData):void =>
        {
            const {surfaceSelection} = controllers.ui;

            const selectedBoxes = surfaceSelection.getSelectedBoxes();

            // if( event.altKey )
            //     surfaceArea.cloneSelectedBoxes();

            selectedBoxes.forEach((box:Box) =>
            {
                const boxElement = this._state.itemRefs.get(box.id);
                const boxClientRect = boxElement.getBoundingClientRect();

                this._state.draggingBoxesPosition.set(box.id, Point.from(boxClientRect.left, boxClientRect.top));
            });
        })
    );

    private _getItemDragHandler = (box:Box):(event:MouseEvent, data:DraggableData) => void => (
        action((event:MouseEvent, data:DraggableData):void =>
        {
            const {surfaceSelection} = controllers.ui;
            const selectedBoxes = surfaceSelection.getSelectedBoxes();
            const {draggingBoxesPosition, isDragging} = this._state;

            if( box.isViewport )
                return;

            if( !surfaceSelection.isBoxSelected(box) )
                return;

            // drag has been canceled
            if( !draggingBoxesPosition.size )
                return;

            if( selectedBoxes.length && !isDragging )
            {
                if( this._isStartedDragAllowed(box) )
                    this._state.isDragging = true;
            }

            selectedBoxes.forEach((box:Box) =>
            {
                const {x, y} = draggingBoxesPosition.get(box.id);

                draggingBoxesPosition.set(box.id, Point.from(x + data.deltaX, y + data.deltaY));
            });
        })
    );

    private _getItemDragStopHandler = (box:Box, hostId:BoxHostId):(event:MouseEvent, data:DraggableData) => void => (
        action((event:MouseEvent, data:DraggableData):void =>
        {
            const {surfaceArea, surfacePagesPanel, surfaceSelection, surfaceBoxReparenting} = controllers.ui;
            const {dragOverBoxCtx} = this._state;

            const currentLayerPage:Page = surfaceArea.getCurrentLayerPage();
            const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

            if( !selectedBoxes.length || !dragOverBoxCtx.boxId )
            {
                this._stopDrag();
                return;
            }

            const targetBox:Box = surfaceArea.findBoxById(currentLayerPage.id, dragOverBoxCtx.boxId);
            const dropIntoBox:Box = dragOverBoxCtx.boxDropPlace === BoxDropPlace.Into ? targetBox : targetBox.parent;

            surfaceBoxReparenting.adjustReparentingBoxesPosition(selectedBoxes, dropIntoBox, null, dragOverBoxCtx.hostId);

            if( dragOverBoxCtx.boxDropPlace === BoxDropPlace.Above )
            {
                let afterBox:Box = surfaceArea.findBoxById(currentLayerPage.id, dragOverBoxCtx.boxId);

                selectedBoxes.forEach((selectedBox:Box) =>
                {
                    surfaceArea.moveAfterBox(selectedBox, afterBox);
                    afterBox = selectedBox;
                });
            }
            else if( dragOverBoxCtx.boxDropPlace === BoxDropPlace.Into )
            {
                const intoBox:Box = surfaceArea.findBoxById(currentLayerPage.id, dragOverBoxCtx.boxId);

                if( dragOverBoxCtx.hostId || !intoBox.componentDesc.isMultiHostContainer )
                {
                    selectedBoxes.forEach((selectedBox:Box) => surfaceArea.moveIntoBox(selectedBox, intoBox, dragOverBoxCtx.hostId));
                    surfacePagesPanel.openOutlineItem(intoBox.id, dragOverBoxCtx.hostId);
                }
                else
                {
                    this._stopDrag();
                    return;
                }
            }
            else if( dragOverBoxCtx.boxDropPlace === BoxDropPlace.Below )
            {
                const beforeBox:Box = surfaceArea.findBoxById(currentLayerPage.id, dragOverBoxCtx.boxId);

                selectedBoxes.forEach((selectedBox:Box) => surfaceArea.moveBeforeBox(selectedBox, beforeBox));
            }

            this._stopDrag();
        })
    );

    @action
    private _stopDrag()
    {
        this._state.isDragging = false;

        this._state.dragOverBoxCtx = {
            boxId: null,
            hostId: null,
            boxDropPlace: null
        };

        this._state.draggingBoxesPosition.clear();
    }

    private _isStartedDragAllowed(box:Box):boolean
    {
        const boxElement = this._state.itemRefs.get(box.id);
        const boxClientRect = boxElement.getBoundingClientRect();

        const {x, y} = this._state.draggingBoxesPosition.get(box.id);

        return (
            Math.abs(x - boxClientRect.left) > SurfaceBoxesOutline.START_DRAGGING_AFTER_DELTA ||
            Math.abs(y - boxClientRect.top) > SurfaceBoxesOutline.START_DRAGGING_AFTER_DELTA
        );
    }

    private static _calculateItemPadding(box:Box, isHostItem:boolean):React.CSSProperties
    {
        const {surfaceArea} = controllers.ui;

        let paddingLeft = 32 + ((surfaceArea.getBoxLevel(box) - 1) * 24);

        if( isHostItem )
            paddingLeft += 24;

        return {paddingLeft};
    }

    private _getCollapseIconMouseDownHandler = (boxId:BoxId, hostId:BoxHostId):(event:React.SyntheticEvent<any>) => void =>
    {
        const {surfacePagesPanel} = controllers.ui;

        return (event:React.SyntheticEvent<any>) =>
        {
            event.stopPropagation();
            surfacePagesPanel.toggleOutlineItem(boxId, hostId);
        };
    };

    private _getItemMouseDownHandler = (box:Box):(event:any) => void => (
        (event:any) =>
        {
            const {surfaceArea} = controllers.ui;

            event.stopPropagation();

            const isShiftKey:boolean = event.shiftKey;
            const isMetaKey:boolean = event.metaKey;

            surfaceArea.changeSelectedBoxesOnBoxClick(isMetaKey, isShiftKey, box);
        }
    );

    private _getItemMouseOverHandler = (box:Box):(event:any) => void => (
        (event:any) =>
        {
            const {surfaceArea} = controllers.ui;
            const {isDragging} = this._state;

            if( isDragging )
                return;

            event.stopPropagation();

            surfaceArea.setMouseOverRef(new MouseOverRef(box, null));
        }
    );

    private _getItemMouseLeaveHandler = (box:Box):(event:any) => void => (
        (event:any) =>
        {
            const {surfaceArea} = controllers.ui;
            const {isDragging} = this._state;

            if( isDragging )
                return;

            event.stopPropagation();

            surfaceArea.setMouseOverRef(null);
        }
    );

    private _getItemDoubleClickHandler = (box:Box):(event:any) => void => (
        action((event:any) =>
        {
            event.stopPropagation();

            if( !box.isViewport )
                this._state.renamingBoxCtx.boxId = box.id;
        })
    );

    private _handleVisibilityEyeClick = (boxVisibilityProp:Prop<ABoolean>, isVisibilityBound:boolean):(event:React.MouseEvent<HTMLElement>)
        => void => (
        (event:React.MouseEvent<HTMLElement>) =>
        {
            event.stopPropagation();

            if( !isVisibilityBound )
                boxVisibilityProp.setPropValue(PropPath.ROOT, new ABoolean(!boxVisibilityProp.value.value));
        }
    );

    private _handleVisibilityEyeDoubleClick = (event:React.MouseEvent<HTMLElement>):void =>
    {
        event.stopPropagation();
    };

    private _handleLockClick = (boxLockProp:Prop<ABoolean>):(event:React.MouseEvent<HTMLElement>)
        => void => (
        (event:React.MouseEvent<HTMLElement>) =>
        {
            event.stopPropagation();
            boxLockProp.setPropValue(PropPath.ROOT, new ABoolean(!boxLockProp.value.value));
        }
    );
}
